import { useState } from "react";
import clinicAPI from "../../../services/clinicApi";
import {
  activationSuccessMessage,
  activationWarningMessage,
} from "../../../utils/membershipActivationMessage";
import s from "../clinic.module.css";
import { Button, Input, Select, useToast } from "../../ui";

function fmtMoney(n) {
  return new Intl.NumberFormat("ru-RU", {
    style: "currency",
    currency: "RUB",
    maximumFractionDigits: 0,
  }).format(Number(n || 0));
}

export default function AcceptMembershipPayment({
  membership,
  patientId,
  onSuccess,
  size = "sm",
}) {
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [onlineBusy, setOnlineBusy] = useState(false);
  const [method, setMethod] = useState("cash");
  const [amount, setAmount] = useState(String(membership?.plan?.price ?? ""));

  if (!membership || membership.status !== "pending") return null;

  const submit = async (e) => {
    e.preventDefault();
    const sum = Number(amount);
    if (!sum || sum <= 0) {
      toast.error("Укажите сумму");
      return;
    }
    setBusy(true);
    try {
      const data = await clinicAPI.createMembershipPayment({
        patient_id: patientId || membership.patient_id,
        membership_id: membership.id,
        amount: sum,
        method,
        paid_at: new Date().toISOString(),
      });
      toast.success(activationSuccessMessage(data.activation));
      const warn = activationWarningMessage(data.activation);
      if (warn) toast.warning(warn);
      setOpen(false);
      onSuccess?.(data);
    } catch (err) {
      toast.error(err.response?.data?.error || "Не удалось принять оплату");
    } finally {
      setBusy(false);
    }
  };

  const payOnline = async () => {
    setOnlineBusy(true);
    try {
      const data = await clinicAPI.initOnlineMembershipPayment({
        patient_id: patientId || membership.patient_id,
        membership_id: membership.id,
      });
      if (data.payment_url) {
        window.open(data.payment_url, "_blank", "noopener,noreferrer");
        toast.success("Страница оплаты открыта");
      }
    } catch (err) {
      toast.error(err.response?.data?.error || "Онлайн-оплата недоступна");
    } finally {
      setOnlineBusy(false);
    }
  };

  if (!open) {
    return (
      <div className={s.membershipPayFormBtns}>
        <Button
          type="button"
          size={size}
          onClick={() => {
            setAmount(String(membership.plan?.price ?? ""));
            setOpen(true);
          }}
        >
          Принять оплату
        </Button>
        <Button
          type="button"
          size={size}
          variant="secondary"
          disabled={onlineBusy}
          onClick={payOnline}
        >
          {onlineBusy ? "…" : "Оплатить онлайн"}
        </Button>
      </div>
    );
  }

  return (
    <form className={s.membershipPayForm} onSubmit={submit}>
      <span className={s.membershipPayFormHint}>
        {membership.plan?.name} · {fmtMoney(membership.plan?.price)}
      </span>
      <Input
        type="number"
        min={0}
        step={1}
        value={amount}
        onChange={(e) => setAmount(e.target.value)}
        aria-label="Сумма"
        disabled={busy}
      />
      <Select
        value={method}
        onChange={(e) => setMethod(e.target.value)}
        disabled={busy}
        aria-label="Способ оплаты"
      >
        <option value="cash">Наличные</option>
        <option value="card_transfer">Перевод</option>
        <option value="online">Онлайн (вручную)</option>
        <option value="other">Другое</option>
      </Select>
      <div className={s.membershipPayFormBtns}>
        <Button type="submit" size={size} disabled={busy}>
          {busy ? "…" : "Подтвердить"}
        </Button>
        <Button
          type="button"
          size={size}
          variant="secondary"
          disabled={busy || onlineBusy}
          onClick={payOnline}
        >
          {onlineBusy ? "…" : "Ссылка T-Bank"}
        </Button>
        <Button
          type="button"
          size={size}
          variant="secondary"
          disabled={busy}
          onClick={() => setOpen(false)}
        >
          Отмена
        </Button>
      </div>
    </form>
  );
}
